/**
 * PhraseCard Component
 * Displays a saved favorite phrase with label, text preview,
 * usage count, last used date, and action buttons.
 */

import React from 'react';
import { FavoritePhrase } from '../../types';
import './PhraseCard.css';

interface PhraseCardProps {
  phrase: FavoritePhrase;
  onClick: (phrase: FavoritePhrase) => void;
  onEdit: (phrase: FavoritePhrase) => void;
  onDelete: (phrase: FavoritePhrase) => void;
}

const formatDate = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return date.toLocaleDateString();
};

const truncateText = (text: string, maxLength: number = 80): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
};

export const PhraseCard: React.FC<PhraseCardProps> = ({
  phrase,
  onClick,
  onEdit,
  onDelete,
}) => {
  return (
    <div
      className="phrase-card"
      onClick={() => onClick(phrase)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(phrase);
        }
      }}
    >
      <div className="phrase-card__header">
        <h4 className="phrase-card__label">{phrase.label}</h4>
        {phrase.targetLang && (
          <span className="phrase-card__lang">
            {phrase.targetLang.toUpperCase()}
          </span>
        )}
      </div>

      <p className="phrase-card__text">{truncateText(phrase.text)}</p>

      {phrase.tags.length > 0 && (
        <div className="phrase-card__tags">
          {phrase.tags.map((tag) => (
            <span key={tag} className="phrase-card__tag">{tag}</span>
          ))}
        </div>
      )}

      <div className="phrase-card__stats">
        <span className="phrase-card__usage">
          Used {phrase.usageCount} {phrase.usageCount === 1 ? 'time' : 'times'}
        </span>
        {phrase.lastUsed && (
          <span className="phrase-card__last-used">
            Last used {formatDate(phrase.lastUsed)}
          </span>
        )}
      </div>

      <div className="phrase-card__actions">
        <button
          className="phrase-card__btn phrase-card__btn--edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(phrase);
          }}
          aria-label={`Edit ${phrase.label}`}
          title="Edit phrase"
        >
          Edit
        </button>
        <button
          className="phrase-card__btn phrase-card__btn--delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(phrase);
          }}
          aria-label={`Delete ${phrase.label}`}
          title="Delete phrase"
        >
          Delete
        </button>
      </div>
    </div>
  );
};
